import { type KeyboardEvent, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import {
  Box,
  Flex,
  Input,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaMagnifyingGlassRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { iconSize } from '~/constants/iconSize'
import { useDropdownDismiss } from '~/hooks/useDropdownDismiss'
import { isEnterKey } from '~/libs/keyboard'
import { visibleAdminHubCategories } from '~/pages/AdminHub/categories'

type Entry = { path: string; label: string; group: string }

const LISTBOX_ID = 'admin-hub-search-menu'
const optionId = (index: number): string => `admin-hub-search-opt-${index}`

/** 一致した部分だけを強調する (design `.hub-search-opt-label mark` = accent / 700)。 */
const Highlighted = ({
  label,
  query,
}: {
  label: string
  query: string
}): React.ReactNode => {
  const i = label.toLowerCase().indexOf(query)
  // 区分名だけで当たった候補は画面名に一致箇所が無いので、 強調せずそのまま出す
  if (i < 0) return label
  return (
    <>
      {label.slice(0, i)}
      <Text
        as="mark"
        textStyle="subSectionTitle"
        color="Text.accentPrimary"
        backgroundColor="transparent"
      >
        {label.slice(i, i + query.length)}
      </Text>
      {label.slice(i + query.length)}
    </>
  )
}

/**
 * システム管理トップの横断検索 (design `admin/index.html` の `.hub-search-*`)。
 *
 * 入力のたびに候補を出し、 選ぶとその管理画面へ移る (incremental combobox)。 ↑↓ で候補を選び、
 * Enter で移る。 外側クリック / Escape で閉じる。
 *
 * **検索対象は画面名と区分名だけ** (どちらもカードと同じ定義 = `routePaths` の name と
 * `page:adminHub.category.*` から引く)。 design は別名 (alias) と画面内の見出し (sections) でも
 * 引けるが、 dev にはその元データが無く、 ハブ専用に書き起こすと文言の出所が増えるため持たない
 * ([design 差異])。
 */
export const AdminHubSearch = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [isOpen, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  useDropdownDismiss(rootRef, isOpen, () => setOpen(false))

  const entries = useMemo<Entry[]>(
    () =>
      visibleAdminHubCategories.flatMap((category) =>
        category.shortcuts.map((shortcut) => ({
          path: shortcut.path,
          label: t(shortcut.nameKey),
          group: t(category.titleKey),
        })),
      ),
    [t],
  )

  const q = query.trim().toLowerCase()
  const hits =
    q === ''
      ? []
      : entries.filter(
          (e) =>
            e.label.toLowerCase().includes(q) ||
            e.group.toLowerCase().includes(q),
        )
  const showMenu = isOpen && q !== ''

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!showMenu) {
        setOpen(true)
        return
      }
      if (hits.length > 0) setActive((a) => (a + 1) % hits.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (hits.length > 0) setActive((a) => (a - 1 + hits.length) % hits.length)
    } else if (isEnterKey(e)) {
      const hit = hits[active]
      if (showMenu && hit !== undefined) {
        e.preventDefault()
        void navigate(hit.path)
      }
    }
  }

  return (
    // design `.hub-search-wrap`: 幅は 360px を基準に伸び、 最大 480px。
    <Box ref={rootRef} position="relative" flex="1 1 360px" maxWidth="480px">
      <Input
        ref={inputRef}
        aria-activedescendant={
          showMenu && hits.length > 0 ? optionId(active) : undefined
        }
        aria-autocomplete="list"
        aria-controls={LISTBOX_ID}
        aria-expanded={showMenu}
        aria-label={t('page:adminHub.search.ariaLabel')}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onFocus={() => {
          if (q !== '') setOpen(true)
        }}
        onKeyDown={handleKeyDown}
        placeholder={t('page:adminHub.search.placeholder')}
        role="combobox"
        style={{ width: '100%', paddingRight: '40px' }}
        type="search"
        value={query}
      />
      {/* design: 虫眼鏡は右端。 入力中は × (クリア) と入れ替える。 */}
      {query === '' ? (
        // 色は design `.hub-search-icon` = --text-mid-emphasis。 light 固定の primitive 値だと
        // dark mode で色が追随しない。
        <Box
          position="absolute"
          top="50%"
          right="12px"
          display="inline-flex"
          color="Text.midEmphasis"
          transform="translateY(-50%)"
          pointerEvents="none"
        >
          <FaMagnifyingGlassRegular
            boxSize={iconSize.gridInline}
            aria-hidden="true"
          />
        </Box>
      ) : (
        // icon-only なので名前を DS Tooltip で出す (`title` は保険として残す)。
        <Box
          position="absolute"
          top="50%"
          right="4px"
          transform="translateY(-50%)"
        >
          <ButtonTooltip content={t('page:adminHub.search.clear')}>
            <TertiaryButton
              width="32px"
              minWidth="32px"
              aria-label={t('page:adminHub.search.clear')}
              onClick={() => {
                setQuery('')
                setOpen(false)
                inputRef.current?.focus()
              }}
              paddingX={0}
              size="sm"
              title={t('page:adminHub.search.clear')}
            >
              <FaXmarkRegular
                boxSize={iconSize.gridInline}
                aria-hidden="true"
              />
            </TertiaryButton>
          </ButtonTooltip>
        </Box>
      )}
      {showMenu && (
        // design `.hub-search-menu`: 入力欄の直下、 影 md、 最大 360px で内側スクロール。
        <Box
          position="absolute"
          zIndex="dropdown"
          top="calc(100% + 4px)"
          right={0}
          left={0}
          overflowY="auto"
          maxHeight="360px"
          padding={1}
          borderWidth="1px"
          borderColor="Border.midEmphasis"
          borderRadius="base"
          boxShadow="shadMd"
          backgroundColor="Surface.primary"
        >
          {hits.length === 0 ? (
            <Text
              textStyle="caption"
              padding={3}
              color="Text.midEmphasis"
              textAlign="center"
            >
              {t('page:adminHub.search.empty')}
            </Text>
          ) : (
            <Flex
              flexDirection="column"
              aria-label={t('page:adminHub.search.listLabel')}
              id={LISTBOX_ID}
              role="listbox"
            >
              {hits.map((hit, index) => (
                <Flex
                  key={hit.path}
                  alignItems="center"
                  justifyContent="space-between"
                  gap={3}
                  textDecoration="none"
                  borderRadius="base"
                  _hover={{ backgroundColor: 'Hover.lowEmphasis' }}
                  aria-selected={index === active}
                  asChild
                  backgroundColor={
                    index === active ? 'Hover.lowEmphasis' : undefined
                  }
                  id={optionId(index)}
                  onMouseEnter={() => setActive(index)}
                  paddingX={3}
                  paddingY={2}
                  role="option"
                >
                  <RouterLink to={hit.path}>
                    <Text textStyle="body" color="Text.highEmphasis">
                      <Highlighted label={hit.label} query={q} />
                    </Text>
                    <Text
                      textStyle="caption"
                      flexShrink={0}
                      color="Text.midEmphasis"
                    >
                      {hit.group}
                    </Text>
                  </RouterLink>
                </Flex>
              ))}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  )
}
